import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  TextField,
  Theme,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import { Close } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { Badge } from '../../../../../../common/badges/Badge';
import {
  BADGE_FRAMES,
  BADGE_UNITS,
  BadgeCentre,
  BadgeDesign,
  BadgeFillStyle,
  BadgeFrame,
  BadgeShape,
  isTailsAvailable,
} from '../../../../../../common/badges/badge-svg';
import { BADGE_SHAPES } from '../../../../../../common/badges/badge-geometry';
import { BADGE_COLOURS, BADGE_COLOUR_GROUPS } from '../../../../../../common/badges/badge-colours';
import { MAX_BADGE_LOGO_BYTES, sanitizeBadgeSvg } from '../../../../../../common/badges/sanitize-badge-logo';
import { IconPicker } from '../../../../../../common/icons/IconPicker';
import { useCampfireTheme } from '../../../../../../theme/useCampfireTheme';

const PRIMARY = '#796FFF';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: { fontSize: '23px', fontWeight: 550, color: theme.color.grey.neutral400, paddingLeft: '5px' },
    content: {
      display: 'flex',
      padding: 0,
      minHeight: '520px',
      [theme.breakpoints.down('sm')]: { flexDirection: 'column' },
    },
    panel: {
      width: '34%',
      minWidth: '300px',
      padding: '20px 24px',
      borderRight: '1px solid #EFEFEF',
      overflowY: 'auto',
      [theme.breakpoints.down('sm')]: { width: '100%', minWidth: 0, borderRight: 0, borderBottom: '1px solid #EFEFEF' },
    },
    viewer: {
      flex: 1,
      background: '#F9F9F9',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    },
    section: { marginBottom: '20px' },
    sectionLabel: { fontSize: '14px', fontWeight: 500, color: theme.color.grey.neutral400, marginBottom: '8px' },
    options: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    tiles: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' },
    option: {
      border: '1px solid #DCDCDC',
      borderRadius: '6px',
      background: '#fff',
      cursor: 'pointer',
      padding: '6px 10px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      color: '#444',
      fontFamily: 'inherit',
      '&:hover': { borderColor: '#9E9E9E' },
      '&:disabled': { opacity: 0.4, cursor: 'default' },
    },
    optionSelected: { borderColor: PRIMARY, boxShadow: `0 0 0 1px ${PRIMARY}`, color: PRIMARY },
    swatchGroup: { fontSize: '12px', color: '#808080', margin: '8px 0 4px' },
    swatch: {
      width: '26px',
      height: '26px',
      borderRadius: '50%',
      border: '2px solid #fff',
      boxShadow: '0 0 0 1px #C4C4C4',
      cursor: 'pointer',
      padding: 0,
    },
    swatchSelected: { boxShadow: `0 0 0 2px ${PRIMARY}` },
    switchRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    helper: { fontSize: '12px', color: '#808080', marginTop: '4px' },
  })
);

interface Props {
  open: boolean;
  title: string;
  design: BadgeDesign;
  onClose: () => void;
  onSave: (design: BadgeDesign) => void;
  onRemove?: () => void;
}

const CENTRES: { value: BadgeCentre; label: string }[] = [
  { value: 'number', label: 'Number' },
  { value: 'icon', label: 'Icon' },
  { value: 'text', label: 'Text' },
  { value: 'logo', label: 'Logo' },
];

const FILLS: { value: BadgeFillStyle; label: string }[] = [
  { value: 'solid', label: 'Solid' },
  { value: 'soft', label: 'Soft' },
  { value: 'outline', label: 'Outline' },
];

const readFile = (file: File, asDataUrl: boolean) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    if (asDataUrl) reader.readAsDataURL(file);
    else reader.readAsText(file);
  });

export const BadgeBuilderDialog = ({ open, title, design, onClose, onSave, onRemove }: Props) => {
  const classes = useStyles();
  const { isMobile } = useCampfireTheme();
  const [state, setState] = useState<BadgeDesign>(design);
  const [logoError, setLogoError] = useState<string>();
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setState(design);
      setLogoError(undefined);
    }
  }, [open, design]);

  const set = (patch: Partial<BadgeDesign>) => setState((prev) => ({ ...prev, ...patch }));

  const onLogo = async (file?: File) => {
    if (!file) return;
    if (file.size > MAX_BADGE_LOGO_BYTES) {
      setLogoError('Logos must be 100KB or smaller');
      return;
    }
    setLogoError(undefined);
    if (file.type === 'image/svg+xml') {
      const logo = sanitizeBadgeSvg(await readFile(file, false));
      if (!logo) {
        setLogoError('That SVG could not be read');
        return;
      }
      set({ logo });
      return;
    }
    if (file.type === 'image/png' || file.type === 'image/jpeg') {
      set({ logo: { kind: 'image', src: await readFile(file, true) } });
      return;
    }
    setLogoError('Use an SVG, PNG or JPG');
  };

  const optionClass = (selected: boolean) => `${classes.option} ${selected ? classes.optionSelected : ''}`;

  return (
    <Dialog open={open} onClose={onClose} disableBackdropClick maxWidth='md' fullWidth fullScreen={isMobile}>
      <DialogTitle disableTypography>
        <Box display='flex' alignItems='center' justifyContent='space-between'>
          <Typography className={classes.title}>{title}</Typography>
          <IconButton aria-label='Close' color='inherit' onClick={onClose}>
            <Close color='action' />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers className={classes.content}>
        <Box className={classes.panel}>
          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Shape</Typography>
            <Box className={classes.tiles}>
              {(Object.keys(BADGE_SHAPES) as BadgeShape[]).map((shape) => (
                <button
                  type='button'
                  key={shape}
                  className={optionClass(state.shape === shape)}
                  onClick={() => set({ shape, tails: isTailsAvailable(shape) ? state.tails : false })}
                >
                  <Badge design={{ ...state, shape, frame: 'none', tails: false }} size={32} />
                  {BADGE_SHAPES[shape].label}
                </button>
              ))}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Frame</Typography>
            <Box className={classes.options}>
              {(Object.keys(BADGE_FRAMES) as BadgeFrame[]).map((frame) => (
                <button
                  type='button'
                  key={frame}
                  className={optionClass(state.frame === frame)}
                  onClick={() => set({ frame })}
                >
                  {BADGE_FRAMES[frame]}
                </button>
              ))}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Centre</Typography>
            <Box className={classes.options} mb={1.5}>
              {CENTRES.map((centre) => (
                <button
                  type='button'
                  key={centre.value}
                  className={optionClass(state.centre === centre.value)}
                  onClick={() => set({ centre: centre.value })}
                >
                  {centre.label}
                </button>
              ))}
            </Box>
            {state.centre === 'number' && (
              <Box display='flex' style={{ gap: '12px' }}>
                <TextField
                  variant='outlined'
                  size='small'
                  label='Number'
                  value={state.value}
                  onChange={(e) => set({ value: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                />
                <FormControl variant='outlined' size='small' style={{ minWidth: 120 }}>
                  <InputLabel shrink>Unit</InputLabel>
                  <Select
                    displayEmpty
                    value={state.unit}
                    onChange={(e) => set({ unit: e.target.value as string })}
                    input={<OutlinedInput notched label='Unit' />}
                  >
                    {BADGE_UNITS.map((unit) => (
                      <MenuItem key={unit || 'none'} value={unit}>
                        {unit || 'No unit'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            {state.centre === 'icon' && <IconPicker value={state.icon} onChange={(icon) => set({ icon })} />}
            {state.centre === 'text' && (
              <TextField
                variant='outlined'
                size='small'
                label='Text'
                helperText='Up to 3 letters'
                value={state.text}
                onChange={(e) =>
                  set({
                    text: e.target.value
                      .replace(/[^A-Za-z0-9]/g, '')
                      .slice(0, 3)
                      .toUpperCase(),
                  })
                }
              />
            )}
            {state.centre === 'logo' && (
              <Box>
                <input
                  ref={fileRef}
                  type='file'
                  accept='image/svg+xml,image/png,image/jpeg'
                  hidden
                  onChange={(e) => {
                    onLogo(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
                <Box display='flex' alignItems='center' style={{ gap: '8px' }}>
                  <TabletButton variant='outlined' color='primary' onClick={() => fileRef.current?.click()}>
                    {state.logo ? 'Replace logo' : 'Upload logo'}
                  </TabletButton>
                  {state.logo && (
                    <TabletButton variant='text' color='error' onClick={() => set({ logo: null })}>
                      Remove
                    </TabletButton>
                  )}
                </Box>
                <Typography className={classes.helper}>SVG, PNG or JPG, up to 100KB</Typography>
                {logoError && (
                  <Typography className={classes.helper} style={{ color: '#f44336' }}>
                    {logoError}
                  </Typography>
                )}
                {state.logo?.kind === 'svg' && (
                  <FormControl variant='outlined' size='small' style={{ minWidth: 180, marginTop: 12 }}>
                    <InputLabel>Logo colours</InputLabel>
                    <Select
                      input={<OutlinedInput label='Logo colours' />}
                      value={state.logoMode}
                      onChange={(e) => set({ logoMode: e.target.value as BadgeDesign['logoMode'] })}
                    >
                      <MenuItem value='match'>Match badge</MenuItem>
                      <MenuItem value='original'>Original colours</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Colour</Typography>
            {BADGE_COLOUR_GROUPS.map((group) => (
              <Box key={group}>
                <Typography className={classes.swatchGroup}>{group}</Typography>
                <Box className={classes.options}>
                  {Object.keys(BADGE_COLOURS)
                    .filter((key) => BADGE_COLOURS[key].group === group)
                    .map((key) => (
                      <button
                        type='button'
                        key={key}
                        title={BADGE_COLOURS[key].name}
                        aria-label={BADGE_COLOURS[key].name}
                        className={`${classes.swatch} ${state.colour === key ? classes.swatchSelected : ''}`}
                        style={{ background: BADGE_COLOURS[key].base }}
                        onClick={() => set({ colour: key })}
                      />
                    ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Fill</Typography>
            <Box className={classes.tiles}>
              {FILLS.map((fill) => (
                <button
                  type='button'
                  key={fill.value}
                  className={optionClass(state.style === fill.value)}
                  onClick={() => set({ style: fill.value })}
                >
                  <Badge design={{ ...state, style: fill.value }} size={32} />
                  {fill.label}
                </button>
              ))}
            </Box>
          </Box>

          <Box className={classes.section}>
            <Typography className={classes.sectionLabel}>Extras</Typography>
            <Box className={classes.switchRow}>
              <Typography style={{ fontSize: 14 }}>Inlay</Typography>
              <Switch color='primary' checked={state.inlay} onChange={(e) => set({ inlay: e.target.checked })} />
            </Box>
            <Box className={classes.switchRow}>
              <Typography style={{ fontSize: 14, opacity: isTailsAvailable(state.shape) ? 1 : 0.5 }}>
                Ribbon tails
              </Typography>
              <Switch
                color='primary'
                disabled={!isTailsAvailable(state.shape)}
                checked={state.tails && isTailsAvailable(state.shape)}
                onChange={(e) => set({ tails: e.target.checked })}
              />
            </Box>
          </Box>
        </Box>

        <Box className={classes.viewer}>
          <Badge design={state} size={220} label={title} />
        </Box>
      </DialogContent>
      <DialogActions>
        {onRemove && (
          <TabletButton variant='text' color='error' onClick={onRemove} style={{ marginRight: 'auto' }}>
            Remove badge
          </TabletButton>
        )}
        <TabletButton variant='text' color='error' onClick={onClose} style={{ marginRight: 8 }}>
          Cancel
        </TabletButton>
        <TabletButton variant='contained' color='primary' onClick={() => onSave(state)}>
          Done
        </TabletButton>
      </DialogActions>
    </Dialog>
  );
};
